import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Check, Copy, ArrowLeft, Send } from 'lucide-react';
import { profileAPI, reviewAPI } from '../services/api';
import toast from 'react-hot-toast';

const AIReviewGeneratorPage = () => {
    const { username } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    
    // Rating state
    const [rating, setRating] = useState(0);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [step, setStep] = useState(1); // 1 = Rating, 2 = Feedback Form, 3 = AI Review Result
    
    // Feedback state (rating <= 3)
    const [category, setCategory] = useState('Service');
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // AI Review state (rating >= 4)
    const [likedFeatures, setLikedFeatures] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedReview, setGeneratedReview] = useState('');
    const [isCopied, setIsCopied] = useState(false);

    const getFeatureOptions = (categories) => {
        const defaultFeatures = ['Service', 'Staff Behaviour', 'Value for Money', 'Experience', 'Quality'];
        if (!categories || categories.length === 0) return defaultFeatures;

        const cat = categories[0];
        
        const categoryMap = {
            "Construction & Contractors": ['Quality of Work', 'Timeliness', 'Professionalism', 'Communication', 'Pricing'],
            "Health & Medicine": ['Care Quality', 'Cleanliness', 'Wait Time', 'Staff Approach', 'Doctor Behavior'],
            "Health & Medical Shops": ['Medicine Availability', 'Staff Assistance', 'Original Products', 'Pricing', 'Store Cleanliness'],
            "Food & Dining": ['Taste', 'Food Quality', 'Ambiance', 'Menu Variety', 'Hygiene'],
            "Food & Beverage Shops": ['Taste', 'Food Quality', 'Ambiance', 'Menu Variety', 'Hygiene'],
            "Merchants (Retail)": ['Product Quality', 'Variety', 'Pricing', 'Staff Assistance', 'Store Ambience'],
            "Clothing & Fashion Shops": ['Collection Variety', 'Fabric Quality', 'Fitting/Trial', 'Staff Assistance', 'Pricing'],
            "Beauty & Personal Care": ['Service Quality', 'Hygiene', 'Staff Skill', 'Ambiance', 'Value for Money'],
            "Automotive": ['Service Quality', 'Transparency', 'Reliability', 'Pricing', 'Mechanic Skill'],
            "Automobile Shops": ['Service Quality', 'Transparency', 'Reliability', 'Pricing', 'Mechanic Skill'],
            "Real Estate": ['Professionalism', 'Market Knowledge', 'Communication', 'Transparency', 'Helpfulness'],
            "Daily Needs & Food Shops": ['Freshness', 'Availability', 'Pricing', 'Staff Behavior', 'Cleanliness'],
            "Computers & Electronics": ['Product Knowledge', 'Technical Support', 'Pricing', 'Product Range', 'Warranty Service'],
            "Electronics & Mobile Shops": ['Product Knowledge', 'Technical Support', 'Pricing', 'Product Range', 'Warranty Service'],
            "Home & Furniture Shops": ['Product Quality', 'Design Variety', 'Durability', 'Pricing', 'Staff Helpfulness'],
            "Specialty Shops": ['Unique Items', 'Product Quality', 'Staff Knowledge', 'Pricing', 'Store Layout'],
            "Education": ['Teaching Quality', 'Curriculum', 'Facilities', 'Staff Support', 'Value for Money'],
            "Legal & Financial": ['Professionalism', 'Expertise', 'Communication', 'Transparency', 'Responsiveness'],
            "Business Support & Supplies": ['Professionalism', 'Expertise', 'Communication', 'Value for Money', 'Efficiency']
        };

        return categoryMap[cat] || defaultFeatures;
    };

    const featureOptions = profile ? getFeatureOptions(profile.categories) : [];

    useEffect(() => {
        fetchProfile();
    }, [username]);

    const fetchProfile = async () => {
        try {
            setIsLoading(true);
            const response = await profileAPI.getPublicProfile(username);
            if (response.data.success && response.data.data) {
                setProfile(response.data.data);
            } else {
                navigate('/404');
            }
        } catch (error) {
            console.error('Failed to fetch profile:', error);
            navigate('/404');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRatingSelect = (selectedRating) => {
        setRating(selectedRating);
        setStep(2); // Move to the next step immediately
    };

    const toggleFeature = (feature) => {
        if (likedFeatures.includes(feature)) {
            setLikedFeatures(likedFeatures.filter(f => f !== feature));
        } else {
            setLikedFeatures([...likedFeatures, feature]);
        }
    };

    const handleGenerateReview = async () => {
        try {
            setIsGenerating(true);
            const response = await reviewAPI.generateReview({
                businessName: profile.company || profile.displayName || username,
                rating,
                likedFeatures,
                optionalComment: comment
            });
            
            if (response.data.success && response.data.data.review) {
                setGeneratedReview(response.data.data.review);
                setStep(3);
                toast.success('Review generated magically!');
            }
        } catch (error) {
            console.error('Error generation review:', error);
            // Provide a graceful fallback
            setGeneratedReview(`I had a ${rating}-star experience here. The ${likedFeatures.length > 0 ? likedFeatures.join(' and ') : 'service'} was great. ${comment}`);
            setStep(3);
            toast.error('AI generation unavailable, using template.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSubmitFeedback = async () => {
        try {
            setIsSubmitting(true);
            await reviewAPI.submitFeedback({
                username,
                rating,
                category,
                comment
            });
            toast.success('Thank you for your feedback. We will improve.');
            // Navigate back to profile
            navigate(`/profile/${username}`);
        } catch (error) {
             console.error('Error submitting feedback:', error);
             toast.error('Failed to submit feedback');
        } finally {
             setIsSubmitting(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(generatedReview);
        setIsCopied(true);
        toast.success('Copied to clipboard!');
        setTimeout(() => setIsCopied(false), 2000);
    };

    const postToGoogle = () => {
        // Need to ensure the profile link is the correct Google Review URL
        const googleLink = profile?.socialLinks?.googleReview || profile?.customLinks?.find(l => l.title?.toLowerCase().includes('review'))?.url;
        
        if (googleLink) {
            window.open(googleLink, '_blank');
        } else {
            toast.error('Google Review link not configured for this profile.');
        }
        
        // Navigate back to profile after a short delay
        setTimeout(() => {
             navigate(`/profile/${username}`);
        }, 1000);
    };

    if (isLoading || !profile) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    // Dynamic Colors based on profile theme or default dark theme
    const bgColor = profile.colors?.background || '#000000';
    const cardColor = profile.colors?.card || '#111111';
    const textColor = profile.colors?.text || '#ffffff';

    return (
        <div 
            className="min-h-screen flex flex-col md:items-center md:justify-center overflow-x-hidden p-4 sm:p-6"
            style={{ backgroundColor: bgColor, color: textColor }}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="relative z-10 w-full md:max-w-md min-h-[500px] rounded-[32px] p-6 shadow-2xl flex flex-col items-center"
                style={{ backgroundColor: cardColor, border: '1px solid rgba(255,255,255,0.1)' }}
            >
                {/* Back Button */}
                <button 
                    onClick={() => {
                        if (step > 1) setStep(1);
                        else navigate(`/profile/${username}`);
                    }}
                    className="absolute top-6 left-6 p-2 bg-white/5 hover:bg-white/10 rounded-full transition-colors"
                >
                    <ArrowLeft className="w-5 h-5 text-gray-300" />
                </button>

                {/* Header Information */}
                <div className="flex flex-col items-center mt-2 mb-8 w-full">
                    <div className="w-20 h-20 rounded-full overflow-hidden mb-4 border-2 border-primary-500/50">
                        {profile.avatar ? (
                            <img src={profile.avatar} alt={profile.displayName} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-800 text-2xl font-bold">
                                {profile.displayName?.charAt(0)}
                            </div>
                        )}
                    </div>
                    <h2 className="text-xl font-bold tracking-tight text-center">
                        Review {profile.company || profile.displayName}
                    </h2>
                </div>

                <AnimatePresence mode="wait">
                    {/* STEP 1: INITIAL RATING */}
                    {step === 1 && (
                        <motion.div
                            key="step1"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="w-full flex flex-col items-center justify-center flex-grow"
                        >
                            <h3 className="text-2xl font-semibold mb-8 text-center bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
                                How was your experience?
                            </h3>
                            <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <motion.button
                                        whileHover={{ scale: 1.2 }}
                                        whileTap={{ scale: 0.9 }}
                                        key={star}
                                        onClick={() => handleRatingSelect(star)}
                                        onMouseEnter={() => setHoveredRating(star)}
                                        onMouseLeave={() => setHoveredRating(0)}
                                        className="p-1 focus:outline-none"
                                    >
                                        <Star 
                                            className={`w-12 h-12 transition-all duration-300 ${
                                                (hoveredRating || rating) >= star 
                                                    ? "text-yellow-400 fill-yellow-400 drop-shadow-[0_0_15px_rgba(250,204,21,0.5)]" 
                                                    : "text-gray-600"
                                            }`} 
                                        />
                                    </motion.button>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {/* STEP 2: DETAILS (NEGATIVE FEEDBACK) */}
                    {step === 2 && rating <= 3 && (
                        <motion.div
                            key="step2-negative"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="w-full flex-grow flex flex-col"
                        >
                            <div className="text-center mb-6">
                                <div className="inline-flex gap-1 mb-4 bg-white/5 p-2 rounded-2xl">
                                     {[1, 2, 3, 4, 5].map((star) => (
                                         <Star key={star} className={`w-5 h-5 ${rating >= star ? "text-yellow-400 fill-yellow-400" : "text-gray-600"}`} />
                                     ))}
                                </div>
                                <h3 className="text-xl font-semibold text-red-400 mb-2">We're sorry..</h3>
                                <p className="text-sm text-gray-400">Please tell us what went wrong so we can improve.</p>
                            </div>

                            <div className="space-y-4 flex-grow">
                                <div>
                                    <label className="text-sm text-gray-400 ml-1">Issue Category</label>
                                    <select 
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        className="w-full bg-[#181818] border border-white/10 rounded-xl py-3 px-4 mt-1 text-white focus:ring-1 focus:ring-primary-500 outline-none"
                                    >
                                        <option value="Service">Poor Service</option>
                                        <option value="Staff Behaviour">Staff Behaviour</option>
                                        <option value="Product Quality">Product Quality</option>
                                        <option value="Price">Overpriced</option>
                                        <option value="Cleanliness">Cleanliness</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="text-sm text-gray-400 ml-1">Additional Details</label>
                                    <textarea 
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        placeholder="Tell us what happened..."
                                        className="w-full bg-[#181818] border border-white/10 rounded-xl py-3 px-4 mt-1 text-white focus:ring-1 focus:ring-primary-500 outline-none h-32 resize-none"
                                    ></textarea>
                                </div>
                            </div>

                            <button
                                onClick={handleSubmitFeedback}
                                disabled={isSubmitting || !comment.trim()}
                                className="w-full mt-6 py-4 bg-gradient-to-r from-red-500 to-rose-600 rounded-xl font-bold shadow-lg shadow-red-500/20 active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {isSubmitting ? 'Sending...' : 'Submit Feedback'}
                                <Send className="w-4 h-4" />
                            </button>
                        </motion.div>
                    )}

                    {/* STEP 2: POSITIVE FEEDBACK (AI GEN PREP) */}
                    {step === 2 && rating >= 4 && (
                        <motion.div
                            key="step2-positive"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="w-full flex-grow flex flex-col"
                        >
                            <div className="text-center mb-6">
                                <div className="inline-flex gap-1 mb-4 bg-white/5 p-2 rounded-2xl">
                                     {[1, 2, 3, 4, 5].map((star) => (
                                         <Star key={star} className={`w-5 h-5 ${rating >= star ? "text-yellow-400 fill-yellow-400" : "text-gray-600"}`} />
                                     ))}
                                </div>
                                <h3 className="text-xl font-semibold text-green-400 mb-2">Glad you liked it!</h3>
                                <p className="text-sm text-gray-400">What were the highlights? (Select options)</p>
                            </div>

                            <div className="flex flex-wrap gap-2 mb-6">
                                {featureOptions.map(feature => (
                                    <button
                                        key={feature}
                                        onClick={() => toggleFeature(feature)}
                                        className={`px-4 py-2 rounded-full border text-sm transition-all duration-300 ${
                                            likedFeatures.includes(feature)
                                            ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                                            : 'bg-transparent border-white/10 text-gray-400 hover:border-white/30'
                                        }`}
                                    >
                                        {feature}
                                    </button>
                                ))}
                            </div>

                            <div className="mb-6 flex-grow">
                                <textarea 
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Anything else to add? (Optional)"
                                    className="w-full bg-[#181818] border border-white/10 rounded-xl py-3 px-4 text-white focus:ring-1 focus:ring-primary-500 outline-none h-24 resize-none"
                                ></textarea>
                            </div>

                            <button
                                onClick={handleGenerateReview}
                                disabled={isGenerating}
                                className="w-full py-4 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl font-bold shadow-lg shadow-blue-500/20 active:scale-95 transition-all flex items-center justify-center gap-2 relative overflow-hidden"
                            >
                                {isGenerating ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                                        <span>Using AI Magic...</span>
                                    </>
                                ) : (
                                    <>
                                        <SparklesIcon className="w-5 h-5" />
                                        <span>Generate Review Text</span>
                                    </>
                                )}
                            </button>
                        </motion.div>
                    )}

                    {/* STEP 3: AI REVIEW RESULT */}
                    {step === 3 && (
                        <motion.div
                            key="step3"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="w-full flex-grow flex flex-col"
                        >
                            <div className="text-center mb-6">
                                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-500/20 text-green-500 rounded-full mb-4">
                                    <Check className="w-6 h-6" />
                                </div>
                                <h3 className="text-xl font-semibold text-white mb-2">Review Ready!</h3>
                                <p className="text-sm text-gray-400">Feel free to edit it below.</p>
                            </div>

                            <div className="relative mb-6 flex-grow">
                                <textarea
                                    value={generatedReview}
                                    onChange={(e) => setGeneratedReview(e.target.value)}
                                    className="w-full bg-[#181818] border border-white/10 rounded-2xl p-4 text-white leading-relaxed focus:ring-1 focus:ring-primary-500 outline-none h-40 resize-none font-medium"
                                />
                                <button 
                                    onClick={copyToClipboard}
                                    className="absolute bottom-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors"
                                >
                                    {isCopied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                                    <span>{isCopied ? 'Copied' : 'Copy'}</span>
                                </button>
                            </div>

                            <div className="bg-blue-500/10 border border-blue-500/20 p-4 rounded-xl mb-6">
                                <p className="text-sm text-blue-200 text-center leading-relaxed">
                                    Next, click below to open Google.<br/>
                                    <strong>Paste</strong> the review and select <strong>{rating} Stars</strong>.
                                </p>
                            </div>

                            <button
                                onClick={postToGoogle}
                                className="w-full py-4 bg-white text-black rounded-xl font-bold shadow-xl active:scale-95 transition-all flex items-center justify-center gap-3"
                            >
                                <GoogleIcon className="w-6 h-6" />
                                Post on Google
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

// Simple Sparkles SVG
const SparklesIcon = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
        <path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>
    </svg>
);

// Minimal Chrome G Icon
const GoogleIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        <path d="M1 1h22v22H1z" fill="none"/>
    </svg>
);

export default AIReviewGeneratorPage;
